import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, mediaUrl } from "../lib/api";
import { Users2, Star, Users as UsersIcon, Map as MapIcon } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";

export default function Clusters() {
    const [clusters, setClusters] = useState([]);
    useEffect(() => { api.get("/clusters").then(({ data }) => setClusters(data)).catch(() => setClusters([])); }, []);

    return (
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">Clusters</h1>
                    <p className="text-muted-foreground mt-2">Tight-knit groups of members, each with a dedicated Liaison.</p>
                </div>
                <div className="flex flex-wrap gap-2 self-start">
                    <Link
                        to="/chapters"
                        className="inline-flex items-center gap-2 rounded-full bg-white text-primary border-2 border-primary px-4 py-2 text-sm font-semibold hover:bg-primary/5 shadow-warm"
                        data-testid="clusters-view-chapters-btn"
                    >
                        <UsersIcon className="h-4 w-4" /> View Chapters
                    </Link>
                    <Link
                        to="/regions"
                        className="inline-flex items-center gap-2 rounded-full bg-white text-primary border-2 border-primary px-4 py-2 text-sm font-semibold hover:bg-primary/5 shadow-warm"
                        data-testid="clusters-view-regions-btn"
                    >
                        <MapIcon className="h-4 w-4" /> View Regions
                    </Link>
                </div>
            </div>

            {clusters.length === 0 ? (
                <div className="mt-16 text-center text-muted-foreground">
                    <Users2 className="h-12 w-12 mx-auto text-slate-300" />
                    <p className="mt-3">No clusters yet. Ask an admin to create one.</p>
                </div>
            ) : (
                <div className="mt-10 grid md:grid-cols-2 gap-5">
                    {clusters.map((c) => <ClusterCard key={c.id} cluster={c} />)}
                </div>
            )}
        </div>
    );
}

function ClusterCard({ cluster }) {
    const l = cluster.liaison;
    const initials = (l?.name || "L").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
    return (
        <div
            className="bg-card rounded-2xl border border-border p-6 shadow-warm"
            data-testid={`cluster-${cluster.id}`}
        >
            <div className="flex items-center gap-4">
                <div
                    className="w-16 h-16 rounded-2xl grid place-items-center text-3xl shrink-0"
                    style={{ backgroundColor: `${cluster.color}22`, color: cluster.color }}
                >
                    <span>{cluster.emoji || "👥"}</span>
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-xl font-bold truncate">{cluster.name}</h3>
                    <div className="text-xs text-muted-foreground">{cluster.member_count} member{cluster.member_count === 1 ? "" : "s"}</div>
                </div>
            </div>

            {cluster.description && (
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{cluster.description}</p>
            )}

            {l ? (
                <Link
                    to={`/directory?member=${l.id}`}
                    className="mt-5 flex items-center gap-3 rounded-xl border-2 border-primary/20 bg-primary/5 p-3 hover:border-primary/50 transition-colors"
                    data-testid={`cluster-liaison-${cluster.id}`}
                >
                    <Avatar className="h-12 w-12 ring-2 ring-primary">
                        {l.avatar_url && <AvatarImage src={mediaUrl(l.avatar_url)} />}
                        <AvatarFallback className="bg-primary/15 text-primary font-bold">{initials}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-primary flex items-center gap-1">
                            <Star className="h-3 w-3" /> Liaison
                        </div>
                        <div className="font-heading font-bold truncate">{l.name}</div>
                        {l.bio && <div className="text-xs text-muted-foreground truncate">{l.bio}</div>}
                    </div>
                </Link>
            ) : (
                <div className="mt-5 rounded-xl bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-muted-foreground">
                    No Liaison assigned yet.
                </div>
            )}
        </div>
    );
}
